import {
    getDepositModeConfigValueForKey, getDepositScheduleValue,
    getDepositScheduleConfigValueForKeyWithState, getDepositModeConfigValueForKeyWithState
} from "../Utils/ConfigUtil";
import {
    calculateNumberOfUnitsFromMonthlyDepositAmount,
    calculateNumberOfUnitsFromYearlyDepositAmount,
    calculateNumberOfUnitsFromLifetimeDepositAmount,
    PLAN_TYPES, PLAN_TYPE_IDS, roundHalfUp, calculatePlanDepositAmount
} from "../Utils/PlanUtils";
import moment from "moment";
import {
    calculateCompletionInsuranceCostForUnits, calculateCriticalIllnessInsuranceCostForUnits,
    calculateNomineeInsuranceCostForUnits,
    calculateNomineeInsurancePerUnitCost
} from "../Utils/InsuranceUtils";
import {
    adjustAirMilesRedemption,
    calculateDiscretionaryEAPAmount, calculateDiscretionaryReturn, calculateEAPAmount,
    loadDepositValuesIntoProjectionArray,
    projectGrants,
    projectIncomeAndFees,
    projectAirMilesRedemptionCoverage
} from "../Utils/ProjectionUtils";

//includeProjectionsInQuote can be used to include the projection array in the quote object. This is useful for debugging and testing.
export const generateQuote = (state, includeProjectionsInQuote=false) => {

    if (state.plan.planTypeID < 0 || state.plan.planTypeID === 100) { return; }
    if (!state.plan) { return; }
    if (!(state.plan.depositCode >= 0)) { return; }
    if (!state.plan.effectiveDate) { return; }

    let quoteAttributes = {};
    quoteAttributes.depositDay = 1; //TODO: This seems to be how it is on th eiPad but maybe we could make it based ont he user's selection
    const depositsPerYear = getDepositModeConfigValueForKeyWithState(state,'NumberOfDepositsPerYear');
    let perUnitCost = 0.0;

    if (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP) {
        quoteAttributes.numberOfAdditionalDeposits = getDepositScheduleConfigValueForKeyWithState(state, 'NumberOfAdditionalDeposits');
    } else if ([PLAN_TYPE_IDS.SI, PLAN_TYPE_IDS.ACHIEVERS].includes(state.plan.planTypeID)) {
        quoteAttributes.numberOfAdditionalDeposits = state.plan.depositCode * depositsPerYear;
        quoteAttributes.numberOfAdditionalDeposits -= 1;
    }

    if (state.plan.effectiveDate && moment(state.plan.effectiveDate).isBefore(moment())) {
        const monthsDelta = moment().diff(moment(state.plan.effectiveDate), 'months');
        //Based on the current month of the year, figure out how many deposits would have been in the year if they
        // had been depositing for the whole year, and remove them from the expected total deposits.
        if (depositsPerYear === 12) {
            quoteAttributes.numberOfAdditionalDeposits -= monthsDelta;
        } else if (depositsPerYear === 1) {
            quoteAttributes.numberOfAdditionalDeposits -= Math.trunc(monthsDelta / 12);
        } else if (depositsPerYear === 2) {
            quoteAttributes.numberOfAdditionalDeposits -= Math.trunc(monthsDelta / 6);
        } else if (depositsPerYear === 4) {
            quoteAttributes.numberOfAdditionalDeposits -= Math.trunc(monthsDelta / 3);
        }
    }



    if (state.quote.quoteBasedOn === 'units') {
        quoteAttributes.numberOfUnits = state.plan.numberOfUnits;
    } else {
        //Work based on deposit amount
        if (state.quote.depositFrequency === 'M') {
            //Monthly deposit
            quoteAttributes.numberOfUnits = calculateNumberOfUnitsFromMonthlyDepositAmount(state, state.plan.depositAmount);
        } else if (state.quote.depositFrequency === 'A') {
            //Yearly deposit
            quoteAttributes.numberOfUnits = calculateNumberOfUnitsFromYearlyDepositAmount(state, state.plan.depositAmount);
        } else if (state.quote.depositFrequency === 'T') {
            //Lifetime deposit
            quoteAttributes.numberOfUnits = calculateNumberOfUnitsFromLifetimeDepositAmount(state, state.plan.depositAmount);
        }
    }
    state.plan.numberOfUnits = quoteAttributes.numberOfUnits;

    /*
    if (state.plan.planTypeID === PLAN_TYPE_IDS.GROUP) {
        perUnitCost = getDepositScheduleConfigValueForKeyWithState(state, "InitialDeposit");
    } else if (state.plan.planTypeID === PLAN_TYPE_IDS.ACHIEVERS) {
        perUnitCost = getDepositScheduleConfigValueForKeyWithState(state, "CostPerUnit");
        perUnitCost /= getDepositModeConfigValueForKeyWithState(state, "NumberOfDepositsPerYear");
        perUnitCost = roundHalfUp(perUnitCost);
    }
     */

    quoteAttributes.amountPerDeposit = calculatePlanDepositAmount(state);
    const completionInsuranceCost = calculateCompletionInsuranceCostForUnits(state, true);
    quoteAttributes.amountPerDeposit += completionInsuranceCost;
    const nomineeInsuranceAmount = calculateNomineeInsuranceCostForUnits(state, true);
    quoteAttributes.amountPerDeposit += nomineeInsuranceAmount;
    const criticalIllnessAmount = calculateCriticalIllnessInsuranceCostForUnits(state, true);
    quoteAttributes.amountPerDeposit += criticalIllnessAmount;

    quoteAttributes = setDefaultQuoteAttributesValues(quoteAttributes);

    //Determine next deposit date
    if (depositsPerYear !== 0) {
        //ongoing deposits
        const depositIntervalInMonths = 12 / depositsPerYear;
        quoteAttributes.nextDepositDate = moment(state.plan.effectiveDate).add(depositIntervalInMonths, "months").toISOString();
        while (moment(quoteAttributes.nextDepositDate).isBefore(moment())) {
            quoteAttributes.nextDepositDate = moment(quoteAttributes.nextDepositDate).add(depositIntervalInMonths, "months").toISOString();
        }
        quoteAttributes.yearlyDepositAmount = quoteAttributes.amountPerDeposit * depositsPerYear;
    } else {
        //One-time deposit
        quoteAttributes.nextDepositDate = null;
        quoteAttributes.yearlyDepositAmount = 0.0;
    }

    let totalInterest = 0.0;
    let projectionArray = loadDepositValuesIntoProjectionArray(state, quoteAttributes);
    projectGrants(state, projectionArray, quoteAttributes);
    projectIncomeAndFees(state, projectionArray, quoteAttributes);



    quoteAttributes.CESGAmount = 0;
    quoteAttributes.ACESGAmount = 0;
    quoteAttributes.CLBAmount = 0;
    quoteAttributes.SAGESAmount = 0.0;
    quoteAttributes.BCTESGAmount = 0.0;
    quoteAttributes.BCTESGInterest = 0.0;
    quoteAttributes.CESGInterest = 0;
    quoteAttributes.ACESGInterest = 0;
    quoteAttributes.CLBInterest  = 0;
    quoteAttributes.QESIAmount = 0;
    quoteAttributes.AQESIAmount = 0;
    quoteAttributes.QESIInterest = 0;
    quoteAttributes.AQESIInterest = 0;
    quoteAttributes.SAGESInterest = 0.0;
    quoteAttributes.enrolmentFee = 0;
    quoteAttributes.depositoryFee = 0;
    quoteAttributes.adminFee = 0;
    quoteAttributes.totalDeposits = 0.0;
    quoteAttributes.airMilesEarned = 0;
    quoteAttributes.planDepositAmount = 0;
    quoteAttributes.planDepositInterest = 0;
    quoteAttributes.interestDeficiency = 0;

    quoteAttributes.totalInsuranceAmount = 0.0; // initialize total insurnace to 0.0 to start

    //After all the projections are done, we loop through each month in the projection array of dictionaries and sum up the different values.
    for (let i = 0; i < 252; i++) {

        //TODO: Somehow these values add up to something other than 2 decimal places...
        quoteAttributes.CESGAmount += projectionArray[i].CESGAmount;
        quoteAttributes.ACESGAmount += projectionArray[i].ACESGAmount;
        quoteAttributes.CLBAmount += projectionArray[i].CLBAmount;
        quoteAttributes.QESIAmount += projectionArray[i].QESIAmount;
        quoteAttributes.AQESIAmount += projectionArray[i].AQESIAmount;
        quoteAttributes.BCTESGAmount += projectionArray[i].BCTESGAmount;

        quoteAttributes.CESGInterest += projectionArray[i].CESGInterest - projectionArray[i].AdminFeeChargeFromCESGInterest;
        quoteAttributes.ACESGInterest += projectionArray[i].ACESGInterest - projectionArray[i].AdminFeeChargeFromACESGInterest;
        quoteAttributes.CLBInterest += projectionArray[i].CLBInterest - projectionArray[i].AdminFeeChargeFromCLBInterest;
        //TODO: In the ipad there appears to be a bug where the following line takes from ACES interest instead of QESI interest
        quoteAttributes.QESIInterest += projectionArray[i].QESIInterest - projectionArray[i].AdminFeeChargeFromQESIInterest;
        quoteAttributes.AQESIInterest += projectionArray[i].AQESIInterest - projectionArray[i].AdminFeeChargeFromAQESIInterest;
        quoteAttributes.BCTESGInterest += projectionArray[i].BCTESGInterest - projectionArray[i].AdminFeeChargeFromBCTESGInterest;


        quoteAttributes.enrolmentFee += projectionArray[i].EnrolmentFeeCharge;
        quoteAttributes.depositoryFee += projectionArray[i].DepositoryFeeCharge;
        quoteAttributes.adminFee += projectionArray[i].AdminFeeCharge;

        quoteAttributes.totalDeposits += projectionArray[i].TotalDepositAmount;

        quoteAttributes.airMilesEarned += projectionArray[i].AirMiles;

        quoteAttributes.planDepositAmount += projectionArray[i].PlanDepositAmount;
        quoteAttributes.planDepositInterest += projectionArray[i].PlanDepositInterest;

        quoteAttributes.interestDeficiency += projectionArray[i].InterestDeficiency;

        /*
        TODO: I believe this is unused code
        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"CESGInterest"] doubleValue];
        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"ACESGInterest"] doubleValue];
        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"QESIInterest"] doubleValue];
        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"AQESIInterest"] doubleValue];
        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"ACESInterest"] doubleValue];
        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"CLBInterest"] doubleValue];
        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"SAGESInterest"] doubleValue];
        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"BCTESGInterest"] doubleValue];

        totalInterest += [[[projectionArray objectAtIndex:i] valueForKey:@"PlanDepositInterest"] doubleValue];
         */


        // Add all the insurance amounts to the total insurance amount
        quoteAttributes.totalInsuranceAmount += projectionArray[i].NIInsuranceAmount;
        quoteAttributes.totalInsuranceAmount += projectionArray[i].PPInsuranceAmount;
        quoteAttributes.totalInsuranceAmount += projectionArray[i].CIInsuranceAmount;
        quoteAttributes.totalInsuranceAmount += projectionArray[i].CPInsuranceAmount;
    }



    quoteAttributes.discretionaryEAPAmount = calculateDiscretionaryEAPAmount(state, quoteAttributes); //Total discretionary EAP amount for the chosen plan.
    quoteAttributes.discretionaryReturn = calculateDiscretionaryReturn(state, quoteAttributes);
    quoteAttributes.EAPAmount = calculateEAPAmount(state, quoteAttributes); //Total EAP amount for the chosen plan (not including discretionary).
    quoteAttributes.airMilesDollarsToRedeem = state.plan.airMilesDollarsToRedeem;

    adjustAirMilesRedemption(state, quoteAttributes);
    projectAirMilesRedemptionCoverage(state, quoteAttributes);


    //calculate age of bene at the end of the plan
    quoteAttributes.beneAgeAtMaturity = moment(state.plan.maturityDate).year() - moment(state.beneficiary.birthdate).year();

    if (includeProjectionsInQuote) {
        quoteAttributes.projections = projectionArray; //TODO: This is just temporary for viewing while we work
    }

    return quoteAttributes;
}

export const setDefaultQuoteAttributesValues = (quoteAttributes) => {

    quoteAttributes.CESGAmount = 0.0; //Total CESG in the quote
    quoteAttributes.ACESGAmount = 0.0; //Total ACESG Amount in the quote
    quoteAttributes.ACESAmount = 0.0; //Total ACESG Amount in the quote
    quoteAttributes.QESIAmount = 0.0; //Total QESI Amount in the quote
    quoteAttributes.AQESIAmount = 0.0; //Total AQESI Amount in the quote
    quoteAttributes.SAGESAmount = 0.0; //Total SAGES Amount in the quote
    quoteAttributes.BCTESGAmount = 0.0; //Total BCTESG Amount in the quote
    quoteAttributes.CLBAmount = 0;

    quoteAttributes.enrolmentFee = 0;
    quoteAttributes.adminFee = 0.0; //Total admin fee in the quote
    quoteAttributes.depositoryFee = 0.0; //Total depository fßee in the quote
    quoteAttributes.CESGInterest = 0.0;
    quoteAttributes.ACESGInterest = 0.0;
    quoteAttributes.ACESInterest = 0.0;
    quoteAttributes.QESIInterest = 0.0;
    quoteAttributes.AQESIInterest = 0.0;
    quoteAttributes.SAGESInterest = 0.0;
    quoteAttributes.BCTESGInterest = 0.0;
    quoteAttributes.CLBInterest  = 0;

    quoteAttributes.totalDeposits = 0;
    quoteAttributes.airMilesEarned = 0;
    quoteAttributes.planDepositAmount = 0;
    quoteAttributes.planDepositInterest = 0;
    quoteAttributes.totalInsuranceAmount = 0.0; // initialize total insurnace to 0.0 to start

    quoteAttributes.amountRequiredOnNextDeposit = 0;

    return quoteAttributes;
}